import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'
import { getFileTagIds } from '@/helpers/file'
import { matchTagsByName } from '@/helpers/tags'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { fileCopyLookupQueryKeys } from '@/hooks/files/types'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { pursuitFilesQueryKeys } from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { File, PotentiallyUnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { Tag } from '@/types/Tag'

export interface CopyFileToLibraryRequest {
  file_id: UUID
  tag_ids?: UUID[]
}

export interface CopyFilesToLibraryRequest {
  files: CopyFileToLibraryRequest[]
  /** When set, copies land in this pursuit's files instead of the org library. */
  pursuit_id?: UUID
}

export interface CopyFileToLibraryResult {
  source_file_id: UUID
  library_file_id?: UUID
  success: boolean
  error?: string
}

export interface CopyFilesToLibraryResponse {
  results: CopyFileToLibraryResult[]
  success_count: number
  failure_count: number
}

interface UseCopyFilesToLibraryArgs {
  orgId: UUID | ''
}

const copyFilesToLibrary = async (
  orgId: UUID | '',
  req: CopyFilesToLibraryRequest,
  accessToken: string,
): Promise<CopyFilesToLibraryResponse> => {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }
  if (req.files.length === 0) {
    return { results: [], success_count: 0, failure_count: 0 }
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/files/copy`,
    {
      method: 'POST',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }

  return (await res.json()) as CopyFilesToLibraryResponse
}

export const useCopyFilesToLibrary = ({ orgId }: UseCopyFilesToLibraryArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<
    CopyFilesToLibraryResponse,
    Error,
    CopyFilesToLibraryRequest
  >({
    mutationFn: (req) => copyFilesToLibrary(orgId, req, accessToken),
    onError: (err) => {
      handleError(err)
    },
    onSuccess: (_res, req) => {
      if (req.pursuit_id) {
        // Pursuit-destination copies land in the pursuit's file list.
        queryClient.invalidateQueries({
          queryKey: pursuitFilesQueryKeys.pursuit({
            pursuitId: req.pursuit_id,
          }),
        })
      } else {
        // Invalidate organization files to show the newly added files
        const key = organizationFilesQueryKeys.organization({ orgId })
        queryClient.invalidateQueries({ queryKey: key })
      }
      // Invalidate file copy lookup to update copied status in UI
      queryClient.invalidateQueries({ queryKey: fileCopyLookupQueryKeys.all() })
    },
  })
}

/**
 * Build a copy request from files, automatically matching source tags to org tags by name.
 *
 * @param files - The files to copy to library
 * @param sourceTags - All source tags (used to look up tag names)
 * @param orgTags - All organization tags (used to find matching tags by name)
 * @returns CopyFilesToLibraryRequest ready to be passed to the mutation
 *
 * @example
 * const { mutate: copyToLibrary } = useCopyFilesToLibrary({ orgId })
 * const { data: sourceTags } = useTags({ orgId, tagType: 'pursuit' })
 * const { data: orgTags } = useTags({ orgId, tagType: 'organization' })
 *
 * // Copy selected files with auto-matched tags
 * const request = buildCopyToLibraryRequest(selectedFiles, sourceTags, orgTags)
 * copyToLibrary(request)
 */
export const buildCopyToLibraryRequest = (
  files: PotentiallyUnhydratedFile[],
  sourceTags: Tag[] | undefined,
  orgTags: Tag[] | undefined,
): CopyFilesToLibraryRequest => {
  return {
    files: files.map((file) => {
      const fileTagIds = getFileTagIds(file)
      const matchedOrgTagIds = matchTagsByName(fileTagIds, sourceTags, orgTags)

      return {
        file_id: file.id,
        tag_ids: matchedOrgTagIds.length > 0 ? matchedOrgTagIds : undefined,
      }
    }),
  }
}
